// VerdeMobility Careers — job listings data
// This is the ONLY file you edit when a job opens, changes, or closes.
//
// STATUS:  "active" = visible everywhere | "discontinued" = hidden, detail page shows a closed message | "draft" = hidden everywhere
// sortOrder: lower numbers show first (leave gaps like 10, 20, 30 so you can slot jobs in between)
// featured: true = eligible for the homepage "Current Openings" preview
// postedDate: YYYY-MM-DD — drives the automatic "New" badge (14 days)
// applyEmail / applicationSubject / applicationNote: build the "Apply Now" mailto link
// overview: sidebar rows on the job detail page
// sections: the body of the job detail page — each has heading + type ("paragraph" | "list" | "labeled-list") + body/items

export const jobs = [

  /* -------------------------------------------------------------------
     JOB: Service Engineer — from VMServiceEngineer_01_JD
     ------------------------------------------------------------------- */
  {
    id: "VMServiceEngineer_01",
    slug: "service-engineer-01",
    title: "Service Engineer",
    department: "operation",
    type: "Full time permanent position",
    employmentTypeShort: "Full-Time",
    vacancies: 1,
    location: "vu-nagar-anand",
    experience: "1-2 Years",
    salary: "Not Disclosed",

    sortOrder: 10,
    status: "active",
    postedDate: "2026-08-18", // TODO: replace with the actual date this role opened
    featured: true,

    teaser: "Install, commission, and maintain EV charging stations — diagnosing hardware, software, and connectivity issues in the field.",

    applyEmail: "career@verdemobility.com",
    applicationSubject: "[VMServiceEngineer_01] Job Application",
    applicationNote: "Application without JOB ID would not be considered.",

    overview: [
      { label: "Department", value: "Operation" },
      { label: "Location", value: "V.U.Nagar, Anand" },
      { label: "Employment Type", value: "Full-Time, Permanent" },
      { label: "Experience", value: "1-2 Years" },
      { label: "Vacancies", value: "1" },
      { label: "Salary", value: "Not Disclosed" }
    ],

    sections: [
      {
        heading: "Education",
        type: "list",
        items: [
          "BE or Diploma"
        ]
      },
      {
        heading: "Skill(s)",
        type: "list",
        items: [
          "Install, commission, and test EV charging stations as per company and regulatory standards.",
          "Conduct preventive and corrective maintenance of EVSE systems to ensure optimal performance.",
          "Diagnose and resolve technical issues related to hardware, software, and network connectivity.",
          "Assist in system upgrades, firmware updates, and configuration of communication interfaces.",
          "Document service activities, prepare maintenance reports, and update service logs.",
          "Ensure all field operations follow safety, environmental, and electrical compliance protocols.",
          "Coordinate with cross-functional teams and provide technical support during issue resolution.",
          "Support customer service teams by providing timely technical feedback and insights.",
          "Willingness to travel and work in field environments."
        ]
      },
      {
        heading: "Additional Skill(s)",
        type: "list",
        items: [
          "Experience in field service, maintenance, or electrical/electronic systems.",
          "Experience in EVSE, power electronics, or renewable energy systems preferred.",
          "Knowledge of AC/DC systems, control circuits, and electrical safety practices.",
          "Familiarity with diagnostic tools (multimeter, insulation tester, etc.) and understanding of communication protocols such as OCPP, Modbus, or TCP/IP is an advantage.",
          "Basic networking and IT troubleshooting abilities.",
          "Strong analytical and problem-solving skills.",
          "Good communication and documentation abilities.",
          "Team-oriented with a proactive and safety-first approach."
        ]
      }
    ]
  },

  /* -------------------------------------------------------------------
     JOB: EV Sales Executive — from VMSalesExecutive_EV_02_JD
     NOTE: the original JD's "Skill(s)" section had run-on bullets that
     read as if several subheadings (Sales & Customer Engagement, Sales
     Process & Documentation, Targets & Reporting, KPIs) collapsed into
     plain bullets when the source Word doc was saved. I reorganized them
     back under those implied subheadings below — please double-check
     nothing was misgrouped.
     ------------------------------------------------------------------- */
  {
    id: "VMSalesExecutive_EV_02",
    slug: "ev-sales-executive-02",
    title: "EV Sales Executive",
    department: "sales-marketing",
    type: "Full time permanent position",
    employmentTypeShort: "Full-Time",
    vacancies: 1,
    location: "remote-multi",
    experience: "3-5 Years",
    salary: "Not Disclosed",

    sortOrder: 20,
    status: "active",
    postedDate: "2026-08-18", // TODO: replace with the actual date this role opened
    featured: true,

    teaser: "Drive EV charger sales across Delhi and South India — from lead generation and demos to closing deals with fleets, builders, and corporates.",

    applyEmail: "career@verdemobility.com",
    applicationSubject: "[VMSalesExecutive_EV_02] Job Application",
    applicationNote: "Application without JOB ID would not be considered.",

    overview: [
      { label: "Department", value: "Sales and Marketing" },
      { label: "Location", value: "Remote - Delhi/Bangalore/Chennai/Hyderabad" },
      { label: "Employment Type", value: "Full-Time, Permanent" },
      { label: "Experience", value: "3-5 Years" },
      { label: "Vacancies", value: "1" },
      { label: "Salary", value: "Not Disclosed" }
    ],

    sections: [
      {
        heading: "Education",
        type: "list",
        items: [
          "BE/B.Tech in Electrical Engineering (preferred)"
        ]
      },
      {
        heading: "Skill(s)",
        type: "labeled-list",
        items: [
          { label: "Sales & Customer Engagement", detail: "Generate and manage leads through walk-ins, telephonic inquiries, digital channels, and field visits. Understand customer charging requirements and recommend suitable EV charging solutions. Present product demonstrations and explain charger features, charging speed, safety standards, and smart connectivity. Build strong relationships with customers, channel partners, fleet operators, builders, and corporate clients. Conduct site visits, customer meetings, and product presentations when required." },
          { label: "Market & Product Knowledge", detail: "Stay updated on EV charging technologies, industry trends, government regulations, and competitor offerings. Educate customers on AC and DC chargers, charging standards, installation requirements, and charging infrastructure. Explain government incentives, EV infrastructure policies, and subsidy schemes applicable in Delhi and Karnataka." },
          { label: "Sales Process & Documentation", detail: "Clearly communicate warranty terms, AMC/service plans, installation process, and after-sales support. Prepare quotations, negotiate pricing, and close sales. Coordinate with technical, installation, logistics, and service teams for timely project execution. Assist customers with documentation, purchase orders, invoicing, and payment follow-ups." },
          { label: "Targets & Reporting", detail: "Ensure compliance with company policies and sales procedures. Achieve monthly and quarterly sales targets. Maintain accurate customer data and sales pipeline in the CRM system. Follow up on inquiries, proposals, and pending orders to maximize conversions. Prepare daily, weekly, and monthly sales reports." },
          { label: "Key Performance Indicators (KPIs)", detail: "Monthly sales revenue and number of EV chargers sold. Lead-to-order conversion ratio. Customer satisfaction and retention. Number of customer meetings, site visits, and product demonstrations conducted. Proposal closure rate and follow-up efficiency." }
        ]
      },
      {
        heading: "Additional Skill(s)",
        type: "list",
        items: [
          "Strong communication and presentation skills (English & Hindi; local language preferred).",
          "Customer-focused with strong negotiation and relationship-building abilities.",
          "Basic understanding of EV charging technology, electrical systems, and charging infrastructure (training will be provided).",
          "Proficiency in CRM software and MS Office.",
          "Prior experience in EV charger, electrical equipment, renewable energy, industrial products, or EV infrastructure sales preferred.",
          "Knowledge of EV charging standards, installation requirements, and electrical systems.",
          "Passion for sustainable mobility and clean energy.",
          "Ability to explain technical concepts in a simple and customer-friendly manner.",
          "Willingness to travel for client meetings and site visits.",
          "Flexibility to work weekends based on business requirements."
        ]
      }
    ]
  },

  /* -------------------------------------------------------------------
     JOB: Purchase Engineer — from VMPurchaseEngineer_EV_03_JD
     ------------------------------------------------------------------- */
  {
    id: "VMPurchaseEngineer_EV_03",
    slug: "purchase-engineer-03",
    title: "Purchase Engineer",
    department: "operation", // TODO: "Procurement" isn't in the new department list — mapped to "Operation", confirm this is right
    type: "Full time permanent position",
    employmentTypeShort: "Full-Time",
    vacancies: 1,
    location: "vu-nagar-anand",
    experience: "1-3 Years",
    salary: "Not Disclosed",

    sortOrder: 30,
    status: "active",
    postedDate: "2026-08-18", // TODO: replace with the actual date this role opened
    featured: true,

    teaser: "Own end-to-end procurement for EV charger components — from vendor sourcing and RFQs to cost analysis and on-time delivery.",

    applyEmail: "career@verdemobility.com",
    applicationSubject: "[VMPurchaseEngineer_EV_03] Job Application",
    applicationNote: "Application without JOB ID would not be considered.",

    overview: [
      { label: "Department", value: "Operation" }, // TODO: confirm same mapping as above
      { label: "Location", value: "V.U.Nagar, Anand" },
      { label: "Employment Type", value: "Full-Time, Permanent" },
      { label: "Experience", value: "1-3 Years" },
      { label: "Vacancies", value: "1" },
      { label: "Salary", value: "Not Disclosed" }
    ],

    sections: [
      {
        heading: "Education",
        type: "list",
        items: [
          "BE Mechanical/Electrical or Diploma in Mechanical/Electrical"
        ]
      },
      {
        heading: "Skill(s)",
        type: "list",
        items: [
          "Handle end-to-end procurement activities including RFQ, price negotiation, and purchase order release.",
          "Source and develop reliable vendors for electronic components, mechanical parts, PCB assemblies, and EV charger-related materials.",
          "Compare quotations and prepare cost analysis for management approval.",
          "Ensure timely delivery of materials to avoid production delays.",
          "Maintain strong relationships with suppliers and negotiate better commercial terms.",
          "Coordinate with design, production, and quality teams for technical specifications and approvals.",
          "Monitor inventory levels and plan procurement accordingly.",
          "Track and follow up on pending orders and resolve supply issues.",
          "Maintain procurement records, documentation, and ERP entries.",
          "Ensure compliance with company policies and statutory requirements (import/export, GST, etc.).",
          "Support cost reduction and localization initiatives."
        ]
      },
      {
        heading: "Additional Skill(s)",
        type: "list",
        items: [
          "Possess strong technical knowledge in electrical switchgear, sheet metal fabrication, powder coating, and sand blasting processes to evaluate vendors and materials effectively."
        ]
      }
    ]
  },

  /* -------------------------------------------------------------------
     JOB: Junior QC Engineer — from VMJuniorQCEngineer_04_JD
     NOTE: the JD's own "Application Guidelines" text specifies the
     subject tag "[VMQCEngineer_EV_04]" (not "VMJuniorQCEngineer_04" as
     the filename suggests) — used that exact tag below so applications
     match whatever filter HR has set up.
     ------------------------------------------------------------------- */
  {
    id: "VMQCEngineer_EV_04",
    slug: "junior-qc-engineer-04",
    title: "Junior QC Engineer",
    department: "quality-assurance",
    type: "Full time permanent position",
    employmentTypeShort: "Full-Time",
    vacancies: 1,
    location: "vu-nagar-anand",
    experience: "2-3 Years",
    salary: "Not Disclosed",

    sortOrder: 40,
    status: "active",
    postedDate: "2026-08-18", // TODO: replace with the actual date this role opened
    featured: true,

    teaser: "Inspect, test, and validate EV AC/DC chargers against IPC/ISO quality standards, driving continuous improvement across the production line.",

    applyEmail: "career@verdemobility.com",
    applicationSubject: "[VMQCEngineer_EV_04] Job Application",
    applicationNote: "Application without JOB ID would not be considered.",

    overview: [
      { label: "Department", value: "Quality Assurance" },
      { label: "Location", value: "V.U.Nagar, Anand" },
      { label: "Employment Type", value: "Full-Time, Permanent" },
      { label: "Experience", value: "2-3 Years" },
      { label: "Vacancies", value: "1" },
      { label: "Salary", value: "Not Disclosed" }
    ],

    sections: [
      {
        heading: "Education",
        type: "list",
        items: [
          "Diploma / BE in Electrical / Electronics & Communication"
        ]
      },
      {
        heading: "Skill(s)",
        type: "list",
        items: [
          "Knowledge of EV AC/DC chargers, electrical systems, and IPC/ISO quality standards.",
          "Strong analytical and problem-solving skills.",
          "Experience in process improvement and continuous improvement initiatives.",
          "Understanding of regulatory compliance and Quality Management Systems (QMS).",
          "Knowledge of EV charger inspection, testing, and validation methods.",
          "Ability to prepare, maintain, and control quality documentation.",
          "Familiarity with manufacturing processes, electrical components, and raw materials.",
          "Good communication, reporting, and documentation skills."
        ]
      },
      {
        heading: "Additional Skill(s)",
        type: "list",
        items: [
          "Familiarity with Quality Management Systems (QMS) and ISO standards.",
          "Ability to work independently as well as collaboratively in a cross-functional team environment.",
          "Strong verbal and written communication and reporting skills.",
          "Demonstrate commitment to continuous improvement and quality excellence."
        ]
      }
    ]
  }

];
